<template>
  <div class="rounded shadow-xs">
    <div class="rounded shadow-md bg-white">
      <div class="px-4 py-3 bg-gray-50 flex justify-between">
        <h2 class="text-base font-medium">
          {{ reviewer.fullName }}
        </h2>
        <span
          class="rounded-full px-3 py-1 text-xs font-medium tracking-wide"
          :class="[
            {
              'text-red-600': isRejected,
              'bg-red-100 bg-opacity-75': isRejected,
              'text-green-600': isApproved,
              'bg-green-100 bg-opacity-75': isApproved,
              'text-white': isPending,
              'bg-black bg-opacity-75': isPending,
            },
          ]"
        >
          <template v-if="isPending">Pending</template>
          <template v-else>{{ isApproved ? 'Approved' : 'Rejected' }}</template>
        </span>
      </div>
      <div class="px-4 py-3 space-y-12">
        <div v-if="hasComments" key="comments" class="space-y-4">
          <h3 class="text-sm font-medium">Comments</h3>
          <ApprovalRequestLineComments v-if="hasComments" :comment-ids="approvalRequest.comments" />
        </div>
        <div v-if="isPending" class="space-y-4">
          <div>
            <ValidationProvider ref="provider" v-slot="{ invalid, errors }">
              <BaseFormGroup>
                <BaseFormLabel> Approve/Reject* Reason </BaseFormLabel>
                <BaseFormTextarea
                  v-model="comments"
                  id="comments"
                  :disabled="loading"
                  rows="5"
                  placeholder="tell us a few words about your opinion..."
                />
                <BaseFormInlineMessage v-if="invalid" danger>
                  {{ errors[0] }}
                </BaseFormInlineMessage>
              </BaseFormGroup>
            </ValidationProvider>
          </div>
          <div class="grid grid-flow-col gap-2 justify-items-stretch">
            <form v-if="canSendApprovalRequest" @submit.prevent="sendEmailNotification">
              <BaseButton
                :primary="!hasSentEmail"
                :ghost="hasSentEmail"
                class="w-full"
                :disabled="loading"
              >
                {{ approvalRequest.emailSentAt ? 'Resend email' : 'Send Email' }}
              </BaseButton>
            </form>
            <form @submit.prevent="approveRequest">
              <BaseButton success class="w-full" :disabled="loading"> Approve </BaseButton>
            </form>
            <form @submit.prevent="rejectRequest">
              <BaseButton danger class="w-full" :disabled="loading"> Reject </BaseButton>
            </form>
          </div>
        </div>
        <div v-if="!isPending" class="">
          <form v-if="canSendApprovalRequest" @submit.prevent="resetRequest">
            <BaseButton ghost class="w-full" :disabled="loading"> Reset </BaseButton>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { approvedApprovalRequestsClient, rejectedApprovalRequestsClient } from '@/api/clients';
import { APPROVAL_REQUESTS } from '@/store/entities/schema_types';
import { ApiClient } from '@/api/client';
import { HTTP_UNPROCESSABLE_ENTITY } from '@/api/http-response-codes';
import ApprovalRequestLineComments from './ApprovalRequestLineComments.vue';

const api = new ApiClient('/api/v1');

const destroyByType = (type) =>
  type === 'approved' ? approvedApprovalRequestsClient : rejectedApprovalRequestsClient;

export default {
  name: 'ApprovalRequestLine',
  components: {
    ApprovalRequestLineComments,
  },
  props: {
    approvalRequestId: {
      type: String,
      required: true,
    },
    canSendApprovalRequest: Boolean,
  },
  data() {
    return {
      loading: false,
      comments: '',
    };
  },
  computed: {
    approvalRequest() {
      if (!(this.approvalRequestId in this.$store.state.entities.approvalRequests.byId)) {
        return null;
      }

      return this.$store.state.entities.approvalRequests.byId[this.approvalRequestId];
    },
    hasComments() {
      return (
        Array.isArray(this?.approvalRequest?.comments) && this.approvalRequest.comments.length > 0
      );
    },
    reviewer() {
      if (!this.approvalRequest) {
        return null;
      }

      if (!(this.approvalRequest.userId in this.$store.state.entities.users.byId)) {
        return null;
      }

      return this.$store.state.entities.users.byId[this.approvalRequest.userId];
    },
    isApproved() {
      return this.approvalRequest.approvedAt !== null;
    },
    isRejected() {
      return this.approvalRequest.rejectedAt !== null;
    },
    isPending() {
      return !this.isApproved && !this.isRejected;
    },
    hasSentEmail() {
      return typeof this.approvalRequest.emailSentAt === 'string';
    },
  },
  methods: {
    async sendEmailNotification() {
      if (this.loading) {
        return;
      }

      try {
        this.loading = true;
        const {
          data: { data },
        } = await api.post(
          `project-assets/${this.approvalRequest.approvableId}/notified-approval-requests`,
          {
            approvalRequestId: this.approvalRequestId,
          }
        );

        this.refreshApprovalRequest(data);
      } catch (error) {
        //
      } finally {
        this.loading = false;
      }
    },
    async approveRequest() {
      try {
        this.loading = true;
        const {
          data: { data },
        } = await approvedApprovalRequestsClient.store({
          approvalRequestId: this.approvalRequestId,
          ...(this.comments ? { comments: this.comments } : {}),
        });

        this.refreshApprovalRequest(data);
        this.resetComments();
      } catch (error) {
        //
      } finally {
        this.loading = false;
      }
    },
    async rejectRequest() {
      try {
        this.loading = true;
        const {
          data: { data },
        } = await rejectedApprovalRequestsClient.store({
          approvalRequestId: this.approvalRequestId,
          comments: this.comments,
        });

        this.$emit('approval-request-rejected', this.approvalRequest);

        this.refreshApprovalRequest(data);
        this.resetComments();
      } catch (error) {
        if (!error.response || error.response.status !== HTTP_UNPROCESSABLE_ENTITY) {
          return;
        }

        if (error.response?.data?.errors?.comments) {
          this.$refs.provider.setErrors(error.response.data.errors.comments);
        }
      } finally {
        this.loading = false;
      }
    },
    async resetRequest() {
      if (this.isPending) {
        return;
      }

      try {
        this.loading = true;
        const {
          data: { data },
        } = await destroyByType(this.isApproved ? 'approved' : 'rejected').destroy(
          this.approvalRequestId
        );

        this.refreshApprovalRequest(data);
      } catch (error) {
        //
      } finally {
        this.loading = false;
      }
    },
    async refreshApprovalRequest(data) {
      try {
        await this.$store.dispatch('entities/persistEntities', { type: APPROVAL_REQUESTS, data });
      } catch (error) {
        //
      } finally {
        this.$emit('updated');
      }
    },
    resetComments() {
      this.comments = '';
    },
  },
};
</script>
